/**
 * WebAudio synth + haptics primitives for Connect 4, Battleship and Beer Pong.
 * Dependency-free on purpose.
 *
 * - Reads/writes the shared per-device preference `awwyeah.games.sound` ('0' = off,
 *   anything else = on) directly, the same key the lead's `./sound.ts` owns — so at merge
 *   `isGameSoundOn`/`setGameSoundOn` here can simply re-export from there.
 * - One lazily-created WebAudio context; every sound is synthesized on the fly (no audio
 *   files to vendor, nothing fetched).
 * - Everything is best effort: blocked audio, no WebAudio, no vibrate — the game just stays
 *   quiet. Nothing here may ever throw into a render.
 */

const SOUND_KEY = 'awwyeah.games.sound';
const listeners = new Set<(on: boolean) => void>();

export function isGameSoundOn(): boolean {
  try {
    return localStorage.getItem(SOUND_KEY) !== '0';
  } catch {
    return true;
  }
}

export function setGameSoundOn(value: boolean): void {
  try {
    localStorage.setItem(SOUND_KEY, value ? '1' : '0');
  } catch {
    // per-device nicety only
  }
  for (const fn of listeners) fn(value);
}

/** Subscribe to toggles made elsewhere on this page (another board's button). */
export function onGameSoundChange(fn: (on: boolean) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

let ctx: AudioContext | null = null;

/** The shared AudioContext, resumed if it was suspended; `null` when audio isn't available. */
export function gameAudio(): AudioContext | null {
  try {
    if (!ctx) {
      const Ctx =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) return null;
      ctx = new Ctx();
    }
    if (ctx.state === 'suspended') void ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

/** Call from a tap so iOS lets later sounds play. */
export function unlockGameAudio(): void {
  gameAudio();
}

/** A context that is allowed to make a sound right now, or `null`. */
function live(): AudioContext | null {
  if (!isGameSoundOn()) return null;
  const ac = gameAudio();
  return ac && ac.state === 'running' ? ac : null;
}

export interface ToneOptions {
  freq: number;
  /** Glide to this frequency over the tone's length. */
  to?: number;
  type?: OscillatorType;
  /** Seconds. */
  dur?: number;
  gain?: number;
  /** Seconds from now. */
  delay?: number;
}

/** One enveloped oscillator note. */
export function tone({
  freq,
  to,
  type = 'sine',
  dur = 0.15,
  gain = 0.15,
  delay = 0,
}: ToneOptions): void {
  const ac = live();
  if (!ac) return;
  try {
    const t = ac.currentTime + delay;
    const osc = ac.createOscillator();
    const g = ac.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t);
    if (to) osc.frequency.exponentialRampToValueAtTime(Math.max(20, to), t + dur);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(gain, t + Math.min(0.01, dur / 4));
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(g);
    g.connect(ac.destination);
    osc.start(t);
    osc.stop(t + dur + 0.05);
  } catch {
    // never load-bearing
  }
}

export interface NoiseOptions {
  /** Seconds. */
  dur?: number;
  gain?: number;
  /** Band-pass centre, Hz; glides to `filterTo` when given. */
  filter?: number;
  filterTo?: number;
  q?: number;
  delay?: number;
  /** Attack time in seconds — longer reads as a "whoosh", shorter as a "crack". */
  attack?: number;
}

/** A burst of filtered white noise: splashes, booms, whooshes, rattles. */
export function noise({
  dur = 0.3,
  gain = 0.3,
  filter = 1200,
  filterTo,
  q = 0.8,
  delay = 0,
  attack = 0.005,
}: NoiseOptions): void {
  const ac = live();
  if (!ac) return;
  try {
    const t = ac.currentTime + delay;
    const length = Math.max(1, Math.floor(ac.sampleRate * dur));
    const buffer = ac.createBuffer(1, length, ac.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i += 1) data[i] = Math.random() * 2 - 1;
    const src = ac.createBufferSource();
    src.buffer = buffer;
    const bp = ac.createBiquadFilter();
    bp.type = 'bandpass';
    bp.Q.value = q;
    bp.frequency.setValueAtTime(filter, t);
    if (filterTo) bp.frequency.exponentialRampToValueAtTime(Math.max(20, filterTo), t + dur);
    const g = ac.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(gain, t + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(bp);
    bp.connect(g);
    g.connect(ac.destination);
    src.start(t);
    src.stop(t + dur + 0.05);
  } catch {
    // never load-bearing
  }
}

/** A small rising chime — wins, "nice shot". */
export function chime(notes: readonly number[] = [660, 830, 990], gap = 0.08): void {
  notes.forEach((freq, i) => tone({ freq, dur: 0.22, gain: 0.14, delay: i * gap }));
}

/** Haptic tap. Follows the sound toggle: "quiet" means no buzzing either. */
export function buzz(pattern: number | number[]): void {
  if (!isGameSoundOn()) return;
  try {
    navigator.vibrate?.(pattern);
  } catch {
    // no haptics here
  }
}
